import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Skeleton,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { LuLandmark, LuScanText, LuSearch, LuTriangleAlert } from 'react-icons/lu'
import { EmptyStateCard } from '@/components/widget-wrappers/EmptyStateCard'
import { OrderStatusPill } from '@/components/order/OrderStatusPill'
import { mapApiStatusToUiStatus } from '@/helper/order.helper'
import { formatDateRange } from '@/utils/date.utils'
import { calculateOrderCommission, type Order } from '@/common/order.common'
import { formatCurrencyEUR, getServiceBadges } from './orderOverview.helpers'

type Props = {
  rows: Order[]
  isLoading?: boolean
  error?: boolean
  onRetry?: () => void
  isFiltered?: boolean
  onSelectOrder?: (order: Order) => void
}

const COLUMNS = [
  { key: 'sib', label: 'SIB', w: '22%' },
  { key: 'scope', label: 'Anzahl bestellter Leistungen', w: '26%' },
  { key: 'period', label: 'Zeitraum', w: '16%' },
  { key: 'gross', label: 'Bestellwert brutto', w: '12%' },
  { key: 'commission', label: 'Provision (10%)', w: '12%' },
  { key: 'status', label: 'Status', w: '12%' },
] as const

const SKELETON_ROWS = 8

function TableShell({ children }: { children: React.ReactNode }) {
  return (
    <Box borderWidth="1px" borderColor="border" borderRadius="xl" overflow="hidden" bg="bg">
      <Table.Root tableLayout="fixed">
        <Table.Header bg="bg">
          <Table.Row>
            {COLUMNS.map((col, idx) => (
              <Table.ColumnHeader
                key={col.key}
                w={col.w}
                px="4"
                py="5"
                fontSize="md"
                fontWeight="700"
                color="fg.default"
                borderBottomWidth="1px"
                borderColor="border"
                borderRightWidth={idx < COLUMNS.length - 1 ? '1px' : '0'}
              >
                {col.label}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>{children}</Table.Body>
      </Table.Root>
    </Box>
  )
}

function EmptyState({ variant }: { variant: 'no-data' | 'no-results' }) {
  const isSearch = variant === 'no-results'
  return (
    <Flex
      minH="200px"
      align="center"
      justify="center"
      borderWidth="1px"
      borderColor="border"
      borderRadius="xl"
      bg="bg"
    >
      <VStack gap={3} px={6} textAlign="center">
        <Icon as={isSearch ? LuSearch : LuScanText} boxSize="28px" color="fg.muted" />
        <Text fontSize="lg" fontWeight="700" color="fg.default">
          {isSearch ? 'Kein Ergebnis' : 'Keine offenen Bestellungen'}
        </Text>
        <Text fontSize="sm" color="fg.muted">
          {isSearch
            ? 'Keine Treffer. Versuchen Sie einen anderen Suchbegriff.'
            : 'Es liegen keine offenen Bestellungen vor.'}
        </Text>
      </VStack>
    </Flex>
  )
}

export function UnionOrderOverviewTable({
  rows,
  isLoading = false,
  error = false,
  onRetry,
  isFiltered = false,
  onSelectOrder,
}: Props) {
  if (error) {
    return (
      <EmptyStateCard
        minH="200px"
        icon={<LuTriangleAlert size={32} />}
        title="Fehler beim Laden"
        description="Die Bestellübersicht konnte nicht geladen werden."
        action={
          onRetry ? (
            <Button variant="outline" onClick={onRetry}>
              Erneut versuchen
            </Button>
          ) : undefined
        }
      />
    )
  }

  if (isLoading) {
    return (
      <TableShell>
        {Array.from({ length: SKELETON_ROWS }).map((_, rowIdx) => (
          <Table.Row key={`skeleton-${rowIdx}`}>
            {COLUMNS.map((col, idx) => (
              <Table.Cell
                key={col.key}
                px="4"
                py="5"
                borderBottomWidth="1px"
                borderColor="border"
                borderRightWidth={idx < COLUMNS.length - 1 ? '1px' : '0'}
                verticalAlign="middle"
              >
                <Skeleton h="16px" rounded="md" />
              </Table.Cell>
            ))}
          </Table.Row>
        ))}
      </TableShell>
    )
  }

  if (rows.length === 0) {
    return <EmptyState variant={isFiltered ? 'no-results' : 'no-data'} />
  }

  return (
    <TableShell>
      {rows.map(order => {
        const badges = getServiceBadges(order.buckets)
        const sibName = order.username ?? order.userName ?? order.userId
        const uiStatus = mapApiStatusToUiStatus(order.status)

        return (
          <Table.Row
            key={order.id}
            _hover={{ bg: 'bg.subtle' }}
            cursor="pointer"
            onClick={() => onSelectOrder?.(order)}
          >
            <Table.Cell
              px="4"
              py="4"
              borderBottomWidth="1px"
              borderRightWidth="1px"
              borderColor="border"
              verticalAlign="middle"
            >
              <HStack gap={2}>
                <Text fontSize="md" fontWeight="500" color="fg.default">
                  {sibName}
                </Text>
                <HStack gap={1} color="fg.muted">
                  <Icon as={LuLandmark} boxSize="3.5" />
                  <Text fontSize="xs">1</Text>
                </HStack>
              </HStack>
            </Table.Cell>

            <Table.Cell
              px="4"
              py="4"
              borderBottomWidth="1px"
              borderRightWidth="1px"
              borderColor="border"
              verticalAlign="middle"
            >
              {badges.length > 0 ? (
                <HStack gap={2} wrap="wrap">
                  {badges.map(b => (
                    <Badge key={b.serviceType} variant="outline" borderRadius="md" px={2} py={1}>
                      {b.value}
                    </Badge>
                  ))}
                </HStack>
              ) : (
                <Text fontSize="sm" color="fg.muted">
                  —
                </Text>
              )}
            </Table.Cell>

            <Table.Cell
              px="4"
              py="4"
              borderBottomWidth="1px"
              borderRightWidth="1px"
              borderColor="border"
              verticalAlign="middle"
            >
              <Text fontSize="md" color="fg.muted">
                {formatDateRange(order.startTime, order.endTime)}
              </Text>
            </Table.Cell>

            <Table.Cell
              px="4"
              py="4"
              borderBottomWidth="1px"
              borderRightWidth="1px"
              borderColor="border"
              verticalAlign="middle"
            >
              <Text fontSize="md" color="fg.muted">
                {order.grossAmount != null ? formatCurrencyEUR(order.grossAmount) : '—'}
              </Text>
            </Table.Cell>

            <Table.Cell
              px="4"
              py="4"
              borderBottomWidth="1px"
              borderRightWidth="1px"
              borderColor="border"
              verticalAlign="middle"
            >
              <Text fontSize="md" color="fg.muted">
                {order.grossAmount != null
                  ? formatCurrencyEUR(calculateOrderCommission(order.grossAmount))
                  : '—'}
              </Text>
            </Table.Cell>

            <Table.Cell
              px="4"
              py="4"
              borderBottomWidth="1px"
              borderColor="border"
              verticalAlign="middle"
            >
              <OrderStatusPill
                status={uiStatus}
                label={uiStatus === 'ABGESENDET' ? 'Offen' : undefined}
              />
            </Table.Cell>
          </Table.Row>
        )
      })}
    </TableShell>
  )
}
